import { useState } from 'react';
import { Anchor, createStyles, Header, Container, Group, Burger, Drawer, Stack } from '@mantine/core';
import { Link } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import { Logo } from './Logo';

const useStyles = createStyles((theme) => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '100%',
  },

  links: {
    [theme.fn.smallerThan('xs')]: {
      display: 'none',
    },
  },

  burger: {
    [theme.fn.largerThan('xs')]: {
      display: 'none',
    },
  },

  link: {
    display: 'block',
    lineHeight: 1,
    padding: '8px 12px',
    borderRadius: theme.radius.sm,
    textDecoration: 'none',
    color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[7],
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,

    '&:hover': {
      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
    },
  },

  linkActive: {
    '&, &:hover': {
      backgroundColor: theme.fn.variant({ variant: 'light', color: theme.primaryColor }).background,
      color: theme.fn.variant({ variant: 'light', color: theme.primaryColor }).color,
    },
  },

  drawer: {
    [theme.fn.largerThan('xs')]: {
      display: 'none',
    },
  },

  mobileLink: {
    display: 'block',
    lineHeight: 1,
    padding: '12px 16px',
    borderRadius: theme.radius.sm,
    textDecoration: 'none',
    color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[7],
    fontSize: theme.fontSizes.md,
    fontWeight: 500,

    '&:hover': {
      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
    },
  },
}));

interface HeaderSimpleProps {
  links: { link: string; label: string, target?: string }[];
}

export function HeaderSimple({ links }: HeaderSimpleProps) {
  const [opened, { toggle }] = useDisclosure(false);
  const [active, setActive] = useState(links[0].link);
  const { classes, cx } = useStyles();

  const items = links.map((link) => (
    <Anchor
      key={link.label}
      to={link.link}
      component={Link}
      target={link.target}
      className={cx(classes.link, { [classes.linkActive]: active === link.link })}
      onClick={(event) => {
        if (! link.link.startsWith("https")) {
          setActive(link.link);
        }
      }}
    >
      {link.label}
    </Anchor>
  ));

  const mobileItems = links.map((link) => (
    <Anchor
      key={link.label}
      to={link.link}
      component={Link}
      target={link.target}
      className={cx(classes.mobileLink, { [classes.linkActive]: active === link.link })}
      onClick={(event) => {
        if (! link.link.startsWith("https")) {
          setActive(link.link);
        }
        toggle(); // Close drawer when link is clicked
      }}
    >
      {link.label}
    </Anchor>
  ));

  return (
    <>
      <Header height={60} mb={120}>
        <Container className={classes.header}>
          <Logo size={28} />
          <Group spacing={5} className={classes.links}>
            {items}
          </Group>

          <Burger opened={opened} onClick={toggle} className={classes.burger} size="sm" />
        </Container>
      </Header>

      <Drawer
        opened={opened}
        onClose={toggle}
        size="100%"
        padding="md"
        position="right"
        className={classes.drawer}
        zIndex={1000000}
      >
        <Stack spacing="md">
          {mobileItems}
        </Stack>
      </Drawer>
    </>
  );
}